import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  ToggleButtonGroup, 
  ToggleButton,
  Alert,
  Snackbar
} from '@mui/material';
import { DEFAULT_POSITION_RANGES } from '../data/ranges';
import './HandTrainer.css';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import StyleIcon from '@mui/icons-material/Style';

const SUITS = ['♠', '♥', '♦', '♣'];
const RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
const POSITIONS = ['Any Position', 'UTG', 'UTG+1', 'UTG+2', 'LOJACK', 'HIJACK', 'CUTOFF', 'BUTTON'];

const PokerCardIcon = () => (
  <svg 
    width="32" 
    height="32" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path 
      d="M4 4C4 2.89543 4.89543 2 6 2H18C19.1046 2 20 2.89543 20 4V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" 
      fill="currentColor"
    />
    <path 
      d="M9 7L12 4L15 7M15 17L12 20L9 17" 
      stroke="white" 
      strokeWidth="1.5"
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
    <path 
      d="M12 4V20" 
      stroke="white" 
      strokeWidth="1.5"
      strokeLinecap="round" 
      strokeLinejoin="round"
    />
  </svg>
);

const HandTrainer = () => {
  const { currentUser } = useAuth();
  const [hand, setHand] = useState(null);
  const [position, setPosition] = useState(null);
  const [userAnswer, setUserAnswer] = useState(null);
  const [result, setResult] = useState(null);
  const [streak, setStreak] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [isTraining, setIsTraining] = useState(false);
  const [sessionStats, setSessionStats] = useState({
    correct: 0,
    incorrect: 0
  });
  const [positionRanges, setPositionRanges] = useState(DEFAULT_POSITION_RANGES);
  const [loading, setLoading] = useState(true);

  const dealNewHand = () => {
    // Generate two random cards
    const card1 = {
      rank: RANKS[Math.floor(Math.random() * RANKS.length)],
      suit: SUITS[Math.floor(Math.random() * SUITS.length)]
    };

    let card2;
    do {
      card2 = {
        rank: RANKS[Math.floor(Math.random() * RANKS.length)],
        suit: SUITS[Math.floor(Math.random() * SUITS.length)]
      };
    } while (card1.rank === card2.rank && card1.suit === card2.suit);

    // If "Any Position" is selected, randomly choose a position
    const actualPosition = selectedPosition === 'Any Position' 
      ? POSITIONS[Math.floor(Math.random() * (POSITIONS.length - 1)) + 1] // +1 to skip "Any Position"
      : selectedPosition;

    // Close any existing snackbar
    setSnackbar(prev => ({ ...prev, open: false }));

    setHand({ card1, card2 });
    setPosition(actualPosition);
    setUserAnswer(null);
    setResult(null);
    setShowAnswer(false);
  };

  useEffect(() => {
    if (isTraining) {
      dealNewHand();
    }
  }, [isTraining]);

  useEffect(() => {
    const loadSavedRanges = async () => {
      if (!currentUser) return;

      try {
        const rangesRef = doc(db, 'users', currentUser.uid, 'settings', 'preflopRanges');
        const docSnap = await getDoc(rangesRef);
        
        if (docSnap.exists()) {
          setPositionRanges({
            ...DEFAULT_POSITION_RANGES, // Fallback for any missing positions
            ...docSnap.data()
          });
        }
      } catch (error) {
        console.error('Error loading saved ranges:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedRanges();
  }, [currentUser]);

  const getHandNotation = () => {
    if (!hand) return '';

    const { card1, card2 } = hand;
    let notation;

    // Sort ranks by their position in the RANKS array (A is first, 2 is last)
    const rankIndex1 = RANKS.indexOf(card1.rank);
    const rankIndex2 = RANKS.indexOf(card2.rank);
    
    // Lower index means higher card (A = 0, K = 1, etc.)
    const [firstRank, secondRank] = rankIndex1 <= rankIndex2 
      ? [card1.rank, card2.rank] 
      : [card2.rank, card1.rank];

    if (firstRank === secondRank) {
      // Pocket pair
      notation = firstRank + firstRank;
    } else {
      // Not a pocket pair - add 's' for suited, 'o' for offsuit
      const suited = card1.suit === card2.suit;
      notation = firstRank + secondRank + (suited ? 's' : 'o');
    }

    return notation;
  };

  const checkAnswer = (answer) => {
    const notation = getHandNotation();
    const isRaiseHand = positionRanges[position]?.coloredCells[notation] === '#ff4444';
    const correctAction = isRaiseHand ? 'raise' : 'fold';
    const isCorrect = answer === correctAction;

    setUserAnswer(answer);
    setResult(isCorrect);
    setShowAnswer(true);
    
    // Update session stats
    setSessionStats(prev => ({
      correct: prev.correct + (isCorrect ? 1 : 0),
      incorrect: prev.incorrect + (isCorrect ? 0 : 1)
    }));

    // Set snackbar message
    setSnackbar({
      open: true,
      message: isCorrect 
        ? "Correct! Keep going!" 
        : `Incorrect. According to the chart, you should ${correctAction} ${notation} from ${position}`,
      severity: isCorrect ? 'success' : 'error'
    });

    if (isCorrect) {
      setStreak(prev => prev + 1);
    } else {
      setStreak(0);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const calculatePercentage = () => {
    const total = sessionStats.correct + sessionStats.incorrect;
    if (total === 0) return 0;
    return ((sessionStats.correct / total) * 100).toFixed(1);
  };

  const Card = ({ rank, suit }) => {
    const getSuitSymbol = (suit) => {
      switch (suit) {
        case '♠': return '♠';
        case '♥': return '♥';
        case '♦': return '♦';
        case '♣': return '♣';
        default: return suit;
      }
    };

    const getCardColor = (suit) => {
      return suit === '♥' || suit === '♦' ? '#e11d48' : '#1e293b';
    };

    return (
      <div className="playing-card" style={{ color: getCardColor(suit) }}>
        <div className="card-value">{rank}</div>
        <div className="card-suit">{getSuitSymbol(suit)}</div>
      </div>
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading your custom ranges...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ color: 'primary.main' }}>
          <PokerCardIcon />
        </Box>
        <Typography variant="h4">Hand Trainer</Typography>
      </Box>
      
      {!isTraining ? (
        <Paper 
          sx={{ 
            p: 4,
            mb: 3,
            backgroundColor: 'background.paper',
            borderRadius: 2,
            maxWidth: 800,
            margin: '0 auto'
          }}
        >
          <Typography 
            variant="h5" 
            gutterBottom 
            align="center"
            sx={{ 
              mb: 4,
              fontWeight: 500,
              color: 'text.primary'
            }}
          >
            Select Position to Practice
          </Typography>
          <Box sx={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 2,
            mb: 4
          }}>
            {POSITIONS.map((pos) => (
              <Button
                key={pos}
                variant={selectedPosition === pos ? "contained" : "outlined"}
                onClick={() => setSelectedPosition(pos)}
                sx={{
                  py: 2,
                  borderColor: selectedPosition === pos ? 'primary.main' : 'grey.500',
                  borderWidth: '2px',
                  backgroundColor: selectedPosition === pos ? 'primary.main' : 'transparent',
                  color: selectedPosition === pos ? 'white' : 'grey.300',
                  '&:hover': {
                    backgroundColor: selectedPosition === pos ? 'primary.dark' : 'rgba(147, 51, 234, 0.1)',
                    borderColor: 'primary.main',
                    borderWidth: '2px',
                    color: selectedPosition === pos ? 'white' : 'primary.main'
                  },
                  transition: 'all 0.2s ease-in-out',
                  fontSize: '0.9rem',
                  fontWeight: 500
                }}
              >
                {pos === 'LOJACK' ? 'Lojack' : pos}
              </Button>
            ))}
          </Box>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center'
          }}>
            <Button
              variant="contained"
              color="primary"
              disabled={!selectedPosition}
              onClick={() => {
                setIsTraining(true);
                dealNewHand();
              }}
              sx={{ 
                py: 1.5,
                px: 4,
                fontSize: '1rem',
                fontWeight: 500,
                minWidth: 200,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4
                },
                '&.Mui-disabled': {
                  backgroundColor: 'rgba(255, 68, 68, 0.2)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  cursor: 'not-allowed'
                }
              }}
            >
              Start Training
            </Button>
          </Box>
        </Paper>
      ) : (
        <>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 3 
            }}>
              <Box>
                <Typography variant="h6" gutterBottom>Current Streak: {streak}</Typography>
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: 2,
                  mt: 2,
                  p: 2,
                  bgcolor: '#2d3748',
                  borderRadius: 1,
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Correct</Typography>
                    <Typography variant="h6" color="success.main">
                      {sessionStats.correct}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Incorrect</Typography>
                    <Typography variant="h6" color="error.main">
                      {sessionStats.incorrect}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Success Rate</Typography>
                    <Typography variant="h6" color="primary.main">
                      {calculatePercentage()}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  setIsTraining(false);
                  setSelectedPosition(null);
                  setStreak(0);
                  setSessionStats({ correct: 0, incorrect: 0 }); // Reset stats
                }}
              >
                Change Position
              </Button>
            </Box>
            
            {hand && (
              <>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 3, 
                  justifyContent: 'center',
                  mb: 4,
                  mt: 2
                }}>
                  <Card rank={hand.card1.rank} suit={hand.card1.suit} />
                  <Card rank={hand.card2.rank} suit={hand.card2.suit} />
                </Box>

                <Typography variant="h5" align="center" gutterBottom>
                  Position: {position}
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                  <ToggleButtonGroup
                    exclusive
                    value={userAnswer}
                    onChange={(e, value) => checkAnswer(value)}
                    disabled={showAnswer}
                  >
                    <ToggleButton value="raise" color="success">
                      Raise
                    </ToggleButton>
                    <ToggleButton value="fold" color="error">
                      Fold
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <Button 
                    variant="contained" 
                    onClick={dealNewHand}
                    disabled={!showAnswer}
                  >
                    Next Hand
                  </Button>
                </Box>
              </>
            )}
          </Paper>
        </>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiAlert-root': {
            width: '100%',
            transition: 'opacity 0.3s ease-in-out'
          }
        }}
      >
        <Alert 
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HandTrainer; 